import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';

const BRAZILIAN_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const AdvancedFilter = ({ onFilterChange, isMobile, onSearchChange, searchTerm = '' }) => {
  const API = process.env.REACT_APP_API.replace(/\/api$/, '');
  const selectedType = useSelector((state) => state.typeFilter);
  
  const [filters, setFilters] = useState({
    propertyType: '',
    state: 'MG',
    city: '',
    neighborhood: '',
    bedrooms: '',
    bathrooms: '',
    garages: '',
    priceMin: '',
    priceMax: '',
  });

  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [isExpanded, setIsExpanded] = useState(!isMobile);

  // Fetch all locations on mount
  useEffect(() => {
    const fetchAllLocations = async () => {
      try {
        const res = await axios.get(`${API}/api/locations`);
        setAllLocations(res.data || []);
      } catch (err) {
        console.error('Erro ao carregar localizações:', err);
        setAllLocations([]);
      }
    };
    fetchAllLocations();
  }, [API]);

  // Update cities when state or allLocations change
  useEffect(() => {
    if (filters.state && allLocations.length > 0) {
      const uniqueCities = allLocations
        .filter(l => l.city && l.city.state === filters.state)
        .map(l => l.city)
        .filter((city, index, self) => self.findIndex(c => c._id === city._id) === index);
      setCities(uniqueCities);
    } else {
      setCities([]);
    }
  }, [filters.state, allLocations]);

  // Update neighborhoods when city or allLocations change
  useEffect(() => {
    if (filters.city && allLocations.length > 0) {
      const filteredNeighborhoods = allLocations.filter(l => l.city && l.city._id === filters.city);
      setNeighborhoods(filteredNeighborhoods);
    } else {
      setNeighborhoods([]);
    }
  }, [filters.city, allLocations]);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    
    // Reset city if state changes
    if (field === 'state') {
      newFilters.city = '';
      newFilters.neighborhood = '';
    }
    
    // Reset neighborhood if city changes
    if (field === 'city') {
      newFilters.neighborhood = '';
    }
    
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchTerm(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const resetFilters = () => {
    setFilters({
      propertyType: '',
      state: '',
      city: '',
      neighborhood: '',
      bedrooms: '',
      bathrooms: '',
      garages: '',
      priceMin: '',
      priceMax: '',
    });
    onFilterChange({});
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <div className="advanced-filter-container">
      <div className="filter-header">
        <div className="search-filter-wrapper">
          <div className="search-input-group">
            <input
              type="text"
              placeholder="Buscar título ou descrição"
              value={localSearchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            <button className="search-button">
              <i className="fas fa-search"></i>
            </button>
            {!isMobile && (
              <button onClick={resetFilters} className="reset-btn">
                <i className="fas fa-times"></i> Limpar
              </button>
            )}
            {isMobile && (
              <button 
                className="filter-toggle-btn"
                onClick={toggleExpanded}
                title="Expandir/Minimizar filtros"
              >
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
              </button>
            )}
            {isMobile && isExpanded && (
              <button onClick={resetFilters} className="reset-btn-compact">
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          
        </div>
      </div>

      {(isExpanded || !isMobile) && (
        <div className="filter-panel">
          <div className="filter-content">
            {/* Campos de Propriedade - apenas para Imóveis */}
            {selectedType === 'imovel' && (
              <>
                {/* Primeira linha - Tipo de Imóvel e Dormitórios */}
                <div className="filter-row">
                  <div className="filter-group compact">
                    <label>Imóvel</label>
                    <select
                      value={filters.propertyType}
                      onChange={(e) => handleFilterChange('propertyType', e.target.value)}
                      className="filter-select"
                    >
                      <option value="">Tipo</option>
                      <option value="apartamento">Apartamento</option>
                      <option value="casa">Casa</option>
                      <option value="condominio">Condomínio</option>
                      <option value="terreno">Terreno</option>
                      <option value="comercial">Comercial</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>

                  <div className="filter-group compact">
                    <label>Dormitórios</label>
                    <select
                      value={filters.bedrooms}
                      onChange={(e) => handleFilterChange('bedrooms', e.target.value)}
                      className="filter-select"
                    >
                      <option value="">Quartos</option>
                      {[1, 2, 3, 4, 5, 6].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group compact">
                    <label>Banheiros</label>
                    <select
                      value={filters.bathrooms}
                      onChange={(e) => handleFilterChange('bathrooms', e.target.value)}
                      className="filter-select"
                    >
                      <option value="">Banheiros</option>
                      {[1, 2, 3, 4, 5].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group compact">
                    <label>Garagens</label>
                    <select
                      value={filters.garages}
                      onChange={(e) => handleFilterChange('garages', e.target.value)}
                      className="filter-select"
                    >
                      <option value="">Garagens</option>
                      {[1, 2, 3, 4, 5].map(num => (
                        <option key={num} value={num}>{num}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* Segunda linha - Localização */}
            <div className="filter-row">
              <div className="filter-group compact">
                <label>Estado</label>
                <select
                  value={filters.state}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                  className="filter-select"
                >
                  <option value="">Estado</option>
                  {BRAZILIAN_STATES.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group compact">
                <label>Cidade</label>
                <select
                  value={filters.city}
                  onChange={(e) => handleFilterChange('city', e.target.value)}
                  disabled={!filters.state}
                  className="filter-select"
                >
                  <option value="">Cidade</option>
                  {cities.map(city => (
                    <option key={city._id} value={city._id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group compact">
                <label>Bairro</label>
                <select
                  value={filters.neighborhood}
                  onChange={(e) => handleFilterChange('neighborhood', e.target.value)}
                  disabled={!filters.city}
                  className="filter-select"
                >
                  <option value="">Bairro</option>
                  {neighborhoods.map(neighborhood => (
                    <option key={neighborhood._id} value={neighborhood._id}>
                      {neighborhood.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group compact">
                <label>Preço Mín.</label>
                <input
                  type="number"
                  placeholder="Mín"
                  value={filters.priceMin}
                  onChange={(e) => handleFilterChange('priceMin', e.target.value)}
                  className="filter-input"
                />
              </div>

              <div className="filter-group compact">
                <label>Preço Máx.</label>
                <input
                  type="number"
                  placeholder="Máx"
                  value={filters.priceMax}
                  onChange={(e) => handleFilterChange('priceMax', e.target.value)}
                  className="filter-input"
                />
              </div>
            </div>

            {isMobile && (
              <div className="filter-actions">
                <button onClick={resetFilters} className="reset-btn-mobile">
                  <i className="fas fa-times"></i> Limpar Filtros
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedFilter;
